import "./timelineHeaderView.scss";

import { List } from "immutable";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withXScrollPositionContext } from "../../components/scrollable/ScrollPositionContext";
import { MIN_CALENDAR_MARKER_PIXEL_SPACING, TIMELINE_HEADER_VIEW_OVERSCROLL } from "../../constants";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import batchOperations from "../../modules/batchOperations";
import { calendarMarkerSelectors } from "../../modules/calendarMarkers";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import dateFunctions from "../../modules/definition/calendar/dateFunctions";
import { isOngoingAllowedForCalendar } from "../../modules/definition/calendar/selectors";
import { typeSelectors } from "../../modules/definition/types";
import { itemSelectors } from "../../modules/items";
import { immutableRecords, PropTypeShapes } from "../../types";
import { disableWhenIos, mergeDispatchToProps } from "../../utils/codeSplit";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import CalendarMarkerView from "./CalendarMarkerView";
import selectors from "./selectors";
import TodayLineHeaderView from "./TodayLineHeaderView";
import timelineViewUtils from "./utils";

// import debugUtils from "../../utils/debugUtils";

const renderLabels = (labels, className, virtualOffset) =>
  labels.map(t => {
    return (
      <div
        key={timelineViewUtils.getTickKey(t)}
        className={className}
        style={{ transform: `translateX(${t.pixel - virtualOffset}px)` }}
      >
        {t.label}
      </div>
    );
  });

const renderCalendarMarkers = (displayMarkers, unitScale, virtualOffset, fontSize, onMarkerClick) =>
  displayMarkers.map(marker => {
    const m = marker.toJS();
    const absLeft = m.startDate.timestamp / unitScale;
    const absRight = m.endDate.timestamp / unitScale;
    const width = absRight - absLeft;
    return (
      <CalendarMarkerView
        {...m}
        width={width}
        fontSize={fontSize}
        key={`${m.id}-${m.startDate.timestamp}`}
        style={{ transform: `translateX(${absLeft - virtualOffset}px)` }}
        onClick={onMarkerClick}
      />
    );
  });

const renderTodayLine = (nowTs, unitScale, virtualOffset) => {
  const absLeft = nowTs / unitScale;
  return <TodayLineHeaderView style={{ transform: `translateX(${absLeft - virtualOffset}px)` }} />;
};

const renderAnniversaries = (displayAnniversaries, unitScale, virtualOffset, fontSize, onItemClick) =>
  displayAnniversaries.map(anniversary => {
    const a = anniversary.toJS();
    const absLeft = a.date.timestamp / unitScale;
    const width = 0;

    return (
      <CalendarMarkerView
        id={a.id}
        label={`${a.itemLabel}: ${timelineViewUtils.ordinalString(a.age)} ${anniversary.anniversaryLabel}`}
        icon={a.anniversaryIcon}
        color={a.color}
        width={width}
        fontSize={fontSize}
        key={`${a.id}-${a.age}`}
        style={{ transform: `translateX(${absLeft - virtualOffset}px)` }}
        onClick={onItemClick}
      />
    );
  });

const renderHover = (dragHoverDate, dragHoverDateString, unitScale, virtualOffset) => {
  if (!dragHoverDate) {
    return null;
  }
  const absLeft = dragHoverDate.timestamp / unitScale;
  const lineStyle = {
    transform: `translateX(${absLeft - virtualOffset}px) translateX(-50%)`
  };
  return (
    <div className="timelineViewDragHoverDate" style={lineStyle}>
      {dragHoverDateString}
    </div>
  );
};

class TimelineHeaderView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.onDoubleClick = this.onDoubleClick.bind(this);
  }

  // componentWillReceiveProps(nextProps) {
  //   debugUtils.logPropChanges("TimelineHeaderView", this.props, nextProps, true);
  // }

  getVisibleLabels(absoluteLeft, absoluteRight) {
    // Get absolute positions with padding so we can do the manual scroll thing.
    const scrollLeft = absoluteLeft - TIMELINE_HEADER_VIEW_OVERSCROLL;
    const scrollRight = absoluteRight + TIMELINE_HEADER_VIEW_OVERSCROLL;
    return selectors.getTimelineHeaderLabels(
      this.props.calendar,
      this.props.viewIds.get(0),
      this.props.xScrollPosition.zoom,
      scrollLeft,
      scrollRight,
      this.props.fontSize,
      this.props.dateRangeFrom,
      this.props.dateRangeTo
    );
  }

  getVisibleMarkers(absoluteLeft, absoluteRight) {
    // Get absolute positions with padding so we can do the manual scroll thing.
    // const scrollLeft = absoluteLeft - TIMELINE_HEADER_VIEW_OVERSCROLL;
    // const scrollRight = absoluteRight + TIMELINE_HEADER_VIEW_OVERSCROLL;
    const zoom = this.props.xScrollPosition.zoom;
    const leftTs = absoluteLeft * zoom;
    const rightTs = absoluteRight * zoom;
    const minRepeatSeconds = MIN_CALENDAR_MARKER_PIXEL_SPACING * zoom;

    return selectors.getTimelineViewDisplayCalendarMarkers(
      this.props.calendarMarkers,
      this.props.calendar,
      leftTs,
      rightTs,
      minRepeatSeconds
    );
  }

  getVisibleAnniversaries(absoluteLeft, absoluteRight) {
    // Get absolute positions with padding so we can do the manual scroll thing.
    const zoom = this.props.xScrollPosition.zoom;
    const scrollLeft = absoluteLeft - TIMELINE_HEADER_VIEW_OVERSCROLL;
    const scrollRight = absoluteRight + TIMELINE_HEADER_VIEW_OVERSCROLL;
    const leftTs = scrollLeft * zoom;
    const rightTs = scrollRight * zoom;
    // TODO: Adjust this repeat limit based on fontSize
    const minRepeatSeconds = MIN_CALENDAR_MARKER_PIXEL_SPACING * zoom;
    const dateForNow = dateFunctions.dateForNow(this.props.calendar);
    return selectors.getTimelineViewDisplayAnniversaries(
      this.props.anniversaryItems,
      this.props.typesById,
      this.props.colorMap,
      this.props.calendar,
      leftTs,
      rightTs,
      minRepeatSeconds,
      dateForNow
    );
  }

  showTodayLine(nowTs, absoluteLeft, absoluteRight) {
    if (this.props.showTodayLine) {
      const zoom = this.props.xScrollPosition.zoom;
      const scrollLeft = absoluteLeft - TIMELINE_HEADER_VIEW_OVERSCROLL;
      const scrollRight = absoluteRight + TIMELINE_HEADER_VIEW_OVERSCROLL;
      const leftTs = scrollLeft * zoom;
      const rightTs = scrollRight * zoom;
      return nowTs > leftTs && nowTs < rightTs;
    }
    return false;
  }

  onDoubleClick(e) {
    // Ignore any double clicks that occur on the item nodes themselves
    if (hasAncestorOfClass(e.target, "calendarMarker")) {
      return;
    }
    // And otherwise add a new event where the double click occurred
    const unitScale = this.props.xScrollPosition.zoom;
    const calendar = this.props.calendar;

    const mouseTs = timelineViewUtils.getMouseTimespan(e, this, this.props.xScrollPosition.canvas.min, unitScale);
    const dragResult = timelineViewUtils.getDropDateResult(calendar, mouseTs, unitScale);
    const dropDate = dragResult.newDate;

    this.props.onCreateCalendarMarkerWithDate(dropDate);
  }

  render() {
    // Calculate left and right bounds
    const viewport = this.props.xScrollPosition.viewport;
    const virtualOffset = this.props.xScrollPosition.canvas.min;
    const unitScale = this.props.xScrollPosition.zoom;
    const labels = unitScale
      ? this.getVisibleLabels(viewport.min, viewport.max)
      : immutableRecords.TimelineHeaderDisplayTicksRecord();

    const displayMarkers = unitScale ? this.getVisibleMarkers(viewport.min, viewport.max) : List([]);
    const displayAnniversaries = unitScale ? this.getVisibleAnniversaries(viewport.min, viewport.max) : List([]);

    const nowTs = dateFunctions.timestampForNow(this.props.calendar);
    const todayLine =
      isOngoingAllowedForCalendar(this.props.calendar) && this.showTodayLine(nowTs, viewport.min, viewport.max)
        ? renderTodayLine(nowTs, unitScale, virtualOffset, this.props.fontSize)
        : null;

    let showCalendarMarkers = true;
    //#!if mobile === true
// showCalendarMarkers = false;
    //#!endif

    return (
      <div className="timelineHeaderView" onDoubleClick={disableWhenIos(this.onDoubleClick)}>
        <div className="timelineHeaderBackground" />
        <div className="timelineHeaderCanvas">
          {renderLabels(labels.major, "majorLabel", virtualOffset)}
          {renderLabels(labels.minor, "minorLabel", virtualOffset)}
          {todayLine}
          {showCalendarMarkers
            ? renderCalendarMarkers(
              displayMarkers,
              unitScale,
              virtualOffset,
              this.props.fontSize,
              this.props.onMarkerClick
            )
            : null}
          {showCalendarMarkers
            ? renderAnniversaries(
              displayAnniversaries,
              unitScale,
              virtualOffset,
              this.props.fontSize,
              this.props.onItemClick
            )
            : null}
          {renderHover(this.props.dragHoverDate, this.props.dragHoverDateString, unitScale, virtualOffset)}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, _props) => {
  return {
    calendar: calendarSelectors.getCalendar(state.present),
    calendarMarkers: calendarMarkerSelectors.getCalendarMarkersList(state.present),
    anniversaryItems: itemSelectors.getItemsWithAnniversaries(state.present),
    colorMap: definitionSelectors.getColorMap(state.present),
    fontSize: appStateSelectors.getFontSize(state.present),
    defaultItemType: definitionSelectors.getDefaultSequenceType(state.present),
    showTodayLine: definitionSelectors.getTimelineViewSettings(state.present).showTodayLine,
    typesById: typeSelectors.getTypesById(state.present),
    dateRangeFrom: selectors.getActualDateFrom(state.present),
    dateRangeTo: selectors.getActualDateTo(state.present)
  };
};

const readonlyActions = {
  onMarkerClick: appStateOperations.selectMarker,
  onItemClick: appStateOperations.selectItems
};

const editingActions = {
  onCreateCalendarMarkerWithDate: date => {
    const batchActions = batchOperations.createCalendarMarkerWithStartDate(
      date.get("timestamp"),
      date.get("precision")
    );
    return batchActions;
  }
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

TimelineHeaderView.propTypes = {
  fontSize: PropTypes.number.isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  calendarMarkers: PropTypeShapes.calendarMarkerList.isRequired,
  anniversaryItems: ImmutablePropTypes.listOf(PropTypes.any).isRequired,
  colorMap: ImmutablePropTypes.map.isRequired,
  viewIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  dateRangeFrom: PropTypeShapes.date,
  dateRangeTo: PropTypeShapes.date,
  showTodayLine: PropTypes.bool.isRequired,
  onMarkerClick: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onCreateCalendarMarkerWithDate: PropTypes.func.isRequired,
  defaultItemType: PropTypes.string.isRequired,
  dragHoverDate: PropTypeShapes.date,
  dragHoverDateString: PropTypes.string,
  typesById: ImmutablePropTypes.map.isRequired,

  // From withScrollPositionContext
  xScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  xScrollActions: PropTypeShapes.scrollActions.isRequired
  // scrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  // scrollActions: PropTypeShapes.scrollActions.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(withXScrollPositionContext(TimelineHeaderView));
